/**
 * The Blackjack Room — the sitting.
 *
 * ============================ WHAT THIS PAGE IS ================================
 * A practice room. Free for every tier, and it stays that way: there are no prizes on
 * this page, no tournament, no leaderboard, and nothing here can be bought. It exists
 * because exact per-decision analysis is a good reason to sit down, and because a room
 * you can wander into is a better introduction to the analytics than a pricing page.
 *
 * COPY DISCIPLINE, and it is load-bearing rather than squeamish. The words "win",
 * "cash", "beat the house" and "casino" do not appear on this page. Chips are chips.
 * `tests/blackjack-copy.test.ts` fails if any of them creeps in, and the same test
 * guards against "solver" and "GTO" — this is enumeration, and "exact" is both true
 * and a stronger claim than either.
 *
 * ========================= PHASE 8: REPLACED, NOT TRIMMED =======================
 * This file was 1,341 lines with its own felt, its own action buttons and a
 * `runBots` loop that played every seat in one synchronous burst. The felt is now
 * `table/BlackjackTable.tsx` on the art kit, the timing is `table/blackjack-director.ts`,
 * the buttons are `ActionBar`'s blackjack variant — so what is left here is the
 * SITTING: the seat card, the money, the grading, the verdict, the Ledger, and the
 * honest copy under the felt. It is the poker route's shape (`Play.tsx`) at a fifth of
 * the size, and the report lists every behaviour of the old file and where it went.
 *
 * Two decks, reshuffled every single hand, which makes counting impossible by
 * construction. That is a deliberate product decision and it is stated on the page.
 * ================================================================================
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { api, ApiError, type BlackjackTotals } from "../lib/api";
import { Card, Notice } from "../components/ui";
import {
  BASE_BET, BUY_IN, CHIP_VALUE, TABLE_MINIMUM, canBeDealt, canCover, inDollars,
} from "@engine/blackjack/money";
/*
 * The NUMBERS come from the engine and the WORDS come from here. The rack is bought
 * from the wallet, so a `$` beside it would be telling somebody their balance is
 * money; `lib/bj-bucks.ts` explains why the formatter moved and the constants did not.
 */
import { bucks as money, signedBucks as signedMoney } from "../lib/bj-bucks";
import { useWallet, BucksAmount } from "../lib/WalletContext";
import { MoneyProvider, useMoney } from "../lib/MoneyContext";
import { Barkeep } from "../rooms/Barkeep";
import { settleAmount, bjOpenHand, bjMaxOut, BJ_STEPPER_SETTLES } from "../lib/wallet-client";
import { BlackjackTable as Engine, type Action } from "@engine/blackjack/table";
import { DOUBLE_DECK_S17 } from "@engine/blackjack/rules";
import { costOf, INDIFFERENCE, type Decision } from "@engine/blackjack/decide";
import { snapshot } from "@engine/blackjack/snapshot";
import { basicAction, type Fidelity } from "@engine/blackjack/bots";
import { rankSlot } from "@engine/blackjack/rules";
import { CryptoRng, randomSeed, sha256, toHex } from "@engine/rng";
import { analyse, type AnalysisResult } from "../lib/analyst";
import { BlackjackTable } from "../table/BlackjackTable";
import {
  BlackjackDirector, type BjDirectorOptions, type BjSnapshot, type BjStep,
} from "../table/blackjack-director";
import { OUTCOME_WORD, totalLabel, type BjView, type HeroMove } from "../table/blackjack-view";
import { blackjackCast } from "../table/blackjack-cast";
import { BjClockLine } from "../table/BjBand";
import { seedFromString } from "../table/director";
import { FlightBus } from "../table/flight";
import { bjDurationsFor } from "../table/pace";
import { getPace, onPaceChange, prefersReducedMotion } from "../table/settings";
import { play as playCue, unlock as unlockSound, type Cue } from "../table/sounds";
import { ActionBar, BetStepper, type BjMove } from "../components/ActionBar";
import { RoomHeader } from "../rooms/RoomShell";
import { roomOf } from "../rooms/rooms";
import { SeatFinder } from "../rooms/SeatFinder";
import { Ledger } from "../rooms/Ledger";
import { bjDealerLine, bjVerdictLine, restrictTo, type BjGraded, type BjVerdict } from "../rooms/bj-copy";
import type { SittingSummary } from "../rooms/copy";
import { useTableView } from "../settings/view";
import { reducedMotionOverride, getPersona } from "../settings/prefs";
import { personaAvatar } from "../settings/PersonaPicker";
import { seatAvatar } from "../art/casting";
import { useUnlocks } from "../lib/useUnlocks";
import { money as usd } from "../lib/api";
import { UNLOCKS } from "@config/plans";

const FIDELITY_LABEL: Record<Fidelity, string> = {
  sharp: "Sharps — everyone plays it correctly",
  casual: "Regulars — the odd slip",
  loose: "Tourists — plenty of slips",
};

/** The stepper's four amounts, in Bucks. Inside the 400 rack; the table minimum first. */
const BJ_STEPS = [25, 50, 100, 200] as const;

/** How a table says an up-card in a sentence: "the dealer's 10", "the dealer's ace". */
const RANK_WORD = ["2", "3", "4", "5", "6", "7", "8", "9", "10", "jack", "queen", "king", "ace"];
const rankWord = (c: number): string => RANK_WORD[c >> 2] ?? "card";

/** "You stood", "You declined insurance" — the free card's words for a move. */
const VERBED: Record<HeroMove, string> = {
  hit: "hit", stand: "stood", double: "doubled", split: "split",
  insurance: "took insurance", decline: "declined insurance",
};
const verbed = (m: HeroMove): string => VERBED[m];
const CHART_VERB: Record<HeroMove, string> = {
  hit: "hits", stand: "stands", double: "doubles", split: "splits",
  insurance: "takes insurance", decline: "declines insurance",
};
const chartVerb = (m: HeroMove): string => CHART_VERB[m];

/** Money is displayed to the cent; the engine's units are not that precise-looking. */
const round2 = (n: number): number => Math.round(n * 100) / 100;

type BotDecision = Parameters<NonNullable<BjDirectorOptions["onBotDecision"]>>[0];

interface Feed { id: number; text: string; kind: "note" | "lesson" | "result" }
/**
 * `betUnits` (Phase 9): every opening bet of the sitting, summed, in 25-Buck units — a
 * hand dealt at 200 adds 8. Counted at the DEAL, so a hand still on the table when the
 * player walks away is in it exactly once, like its `hands` and `net` are. The Worker
 * bounds the sitting's swing by it (`bjSwing`), which is what lets the stepper open.
 */
interface Session { hands: number; decisions: number; correct: number; given: number; givenBucks: number; net: number; betUnits: number }
const NO_SESSION: Session = { hands: 0, decisions: 0, correct: 0, given: 0, givenBucks: 0, net: 0, betUnits: 0 };

/**
 * What a card in the room's Play tab hands this page: how many seats, who is in them,
 * and the card's own name for the header and the Ledger. The Play tab is a front door
 * and not a second table.
 */
export interface BlackjackPreset { seats?: number; fidelity?: Fidelity; title?: string; cardId?: string }

/** A sound for a step, from the kit's own cues. Silence is part of the rhythm. */
function bjCue(step: BjStep): Cue | null {
  switch (step.kind) {
    case "shuffle": return "shuffle";
    case "deal": return "deal";
    case "bet": case "chips": case "insure": return "chip-slide";
    case "flip": return "flip";
    case "settle": case "insurance": return step.returned > 0 ? "pot-push" : null;
    default: return null;
  }
}

const bjMotionNow = () => bjDurationsFor(getPace(), prefersReducedMotion() || reducedMotionOverride());

export function Blackjack({ preset }: { preset?: BlackjackPreset } = {}) {
  /*
   * One provider around the whole screen, PINNED to Bucks: blackjack has no big blind,
   * and the poker toggle's "bb" would print a green chip as "1bb".
   */
  return (
    <MoneyProvider bigBlind={CHIP_VALUE} format="bucks" pin="bucks">
      <Room preset={preset ?? {}} />
    </MoneyProvider>
  );
}

function Room({ preset }: { preset: BlackjackPreset }) {
  const navigate = useNavigate();
  const { fmt } = useMoney();
  const seats = preset.seats === 1 || preset.seats === 5 ? preset.seats : 3;
  const fidelity: Fidelity = preset.fidelity ?? "casual";
  const title = preset.title ?? "Blackjack";
  const room = roomOf("blackjack")!;

  const [studyMode, setStudyMode] = useState(false);
  /*
   * PHASE 9 (master plan §9): the exact numbers — every option's expected value, what a
   * choice gave up, study mode, and the record across sittings — are Blackjack Pro,
   * $9.99 once or included in Poker Pro. The table, the chart's verdict and the Ledger
   * are free. `exact` is false while the answer is still being asked for: a paid number
   * is never printed on a guess.
   */
  const unlocks = useUnlocks();
  const exact = unlocks.has("blackjack_pro");
  const exactRef = useRef(exact);
  exactRef.current = exact;
  const studyOn = studyMode && exact;
  const [showLessons, setShowLessons] = useState(true);

  /* ------------------------------------------------------------ a sitting --- */

  /**
   * One sitting: a seed, the table dealt from it, the bots' generator, the cast.
   *
   * A SESSION seed verifies a whole sitting on `/fair`, hand by hand, from one recorded
   * value — so sitting down draws it and every hand derives from it. "Play again" from
   * the Ledger is a new sitting with its own seed, its own receipt and its own cast.
   */
  const [sittingNo, setSittingNo] = useState(0);
  const sitting = useMemo(() => {
    const seed = randomSeed();
    const seedHex = toHex(seed);
    return {
      seedHex,
      commitment: toHex(sha256(seed)),
      cast: blackjackCast(seedHex, seats, getPersona()),
      table: new Engine({
        rules: DOUBLE_DECK_S17,
        seed,
        seats: Array.from({ length: seats }, (_, i) => ({ id: `s${i}`, isHero: i === 0 })),
      }),
      botRng: new CryptoRng(seed, "bots"),
      paceSeed: seedFromString(`${seedHex}/pace`),
    };
    // A new sitting is exactly a new `sittingNo`; the seats and fidelity key the route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sittingNo]);
  const table = sitting.table;
  const seedHex = sitting.seedHex;
  const cast = sitting.cast;
  /* Read by the exits (`pagehide`, unmount), which hold the first render's closure. */
  const tableRef = useRef(table);
  tableRef.current = table;
  /** The last hand whose result reached `session` — see `withOpenHand`. */
  const countedHand = useRef(0);
  /**
   * Which sitting a save belongs to. "Play again" can start the next sitting while the
   * last one's settlement is still in flight; a late reply must not write its ticket,
   * its note or its money state into the sitting that replaced it.
   */
  const sittingGen = useRef(0);

  const [screen, setScreen] = useState<"seat" | "table" | "ledger">("seat");
  const screenRef = useRef(screen);
  screenRef.current = screen;
  const [snap, setSnap] = useState<BjSnapshot | null>(null);
  const view: BjView | null = snap?.view ?? null;
  const [flights] = useState(() => new FlightBus<BjStep, BjView>());
  const [motion, setMotion] = useState(bjMotionNow);
  const motionRef = useRef(motion);
  motionRef.current = motion;
  useEffect(() => onPaceChange(() => setMotion(bjMotionNow())), []);
  const [shoe, setShoe] = useState<"full" | "shuffling">("full");
  const [tray, setTray] = useState(0);
  /* The dealer stands on a wide desktop window — the saloon's own rule — and is a disc
     everywhere else. */
  const { fits: standingDealer } = useTableView();

  /**
   * THE STACK, which carries. It does not reset between hands — a stack that is down
   * to seventy-five after eleven hands has taught the player what a sixteen-hand
   * bankroll is, which no strategy chart can. Bucks; the engine's units are converted
   * at this boundary and nowhere else.
   */
  const [stack, setStack] = useState(BUY_IN);
  /** How many racks have been taken, this sitting. Shown, and shown honestly. */
  const [racks, setRacks] = useState(1);
  /**
   * The bet, in Bucks. The stepper is drawn with its four amounts; while
   * `BJ_STEPPER_SETTLES` is off only the first is open, because the Worker bounds a
   * sitting's swing at one unit a hand — see that constant for the argument.
   */
  const [bet, setBet] = useState<number>(BASE_BET);
  const stake = BJ_STEPPER_SETTLES ? bet : BASE_BET;

  const [session, setSession] = useState<Session>(NO_SESSION);
  /**
   * The running totals, written in the same breath as the change rather than read back
   * off the next render. The exits read this: a tab closed in the milliseconds between a
   * hand ending and React drawing it would otherwise settle without that hand, while
   * `countedHand` already said it was counted.
   */
  const sessionRef = useRef<Session>(NO_SESSION);
  const commitSession = (next: Session) => { sessionRef.current = next; setSession(next); };
  /** The settlement in flight, so "Play again" can wait for it rather than race it. */
  const inFlight = useRef<Promise<void> | null>(null);
  const againing = useRef(false);
  const [feed, setFeed] = useState<Feed[]>([]);
  const [analysis, setAnalysis] = useState<Decision | null>(null);
  const [analysing, setAnalysing] = useState(false);
  const [slowMs, setSlowMs] = useState<number | null>(null);
  const [inlineFallback, setInlineFallback] = useState(false);
  const [lastCall, setLastCall] = useState<{ d: Decision; took: HeroMove; chart?: HeroMove } | null>(null);
  const [verdict, setVerdict] = useState<BjVerdict | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [saveNote, setSaveNote] = useState<string | null>(null);
  const [history, setHistory] = useState<BlackjackTotals | null>(null);
  const [barkeep, setBarkeep] = useState(false);
  const [rackShort, setRackShort] = useState(false);
  /** A rebuy is in flight: the button is off, so a double tap cannot buy two racks. */
  const [rebuying, setRebuying] = useState(false);
  const rebuyingRef = useRef(false);
  /** The Ledger's three money states — see `LedgerProps.money`. */
  const [settled, setSettled] = useState<{ paid: number; credited: number | null } | null>(null);
  const [settling, setSettling] = useState(false);

  const director = useRef<BlackjackDirector | null>(null);
  const feedId = useRef(0);
  /** Bumped per hand; a late analysis for an older hand is thrown away against it. */
  const handSeq = useRef(0);
  /** What the hand on the table was dealt at, in Bucks. The stepper may move after. */
  const handStake = useRef(BASE_BET);
  /** This hand's graded decisions, and the grades still waiting on an analysis. */
  const graded = useRef<BjGraded[]>([]);
  const pending = useRef<Promise<unknown>[]>([]);
  /** Bot lessons, by the ordinal of the act they describe: shown when that act is. */
  const lessons = useRef(new Map<number, { text: string | null; shown: boolean; slot: number }>());
  /** The analysis for the position on screen — see the old room's note, unchanged. */
  const live = useRef<{ id: number; result: Promise<AnalysisResult> } | null>(null);

  /* ------------------------------------------------------------ the feed --- */

  const push = useCallback((text: string, kind: Feed["kind"] = "note") => {
    setFeed((f) => [{ id: feedId.current++, text, kind }, ...f].slice(0, 40));
  }, []);

  /**
   * Put a line in the feed at a position reserved earlier. Bot lessons are analysed
   * off-thread and land out of order; the id is taken when the bot acts, so the feed
   * reads in the order the table played.
   */
  const pushAt = useCallback((id: number, text: string, kind: Feed["kind"]) => {
    setFeed((f) => {
      const next = [...f];
      const at = next.findIndex((x) => x.id < id);
      next.splice(at === -1 ? next.length : at, 0, { id, text, kind });
      return next.slice(0, 40);
    });
  }, []);

  /* -------------------------------------------------------- Buster Bucks --- */

  /**
   * The rack is a BUY-IN, and a rebuy is a second one — on the same one-write-per-
   * sitting contract as the poker table: the server debits and hands back a ticket at
   * sit-down, a rebuy presents that ticket and gets a replacement carrying the
   * cumulative total on the SAME nonce, and the whole sitting settles once when the
   * player leaves. Buster Bucks are practice money; they refill, and a player who runs
   * out is staked by the Barkeep.
   */
  const { isSignedIn, getToken, userId } = useAuth();
  const isSignedInRef = useRef(isSignedIn);
  isSignedInRef.current = isSignedIn;
  const wallet = useWallet();
  const walletRef = useRef(wallet);
  walletRef.current = wallet;
  /* `getToken` off a ref, never in a dependency list: it is a new closure every render. */
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const ticket = useRef<{ receipt: string | null; buyin: number } | null>(null);
  /** Everything this sitting has paid, surviving the ticket being cleared. */
  const paid = useRef(0);
  const buying = useRef(false);
  const saved = useRef(false);
  const sittingStart = useRef(Date.now());

  /**
   * Buy one rack, or a rebuy, and say plainly which happened. ONE SEAT PER SITTING:
   * holding a ticket is the record that this sitting is paid for, the guard is set
   * before the await, and only a rebuy is let past it. A failure that is not a refusal
   * deals anyway, unfunded — somebody who came here to practise gets to practise.
   */
  const purchase = useCallback(async (
    opts: { rebuy?: boolean } = {},
  ): Promise<{ ok: true } | { ok: false; cost: number; balance: number }> => {
    if (!opts.rebuy && (ticket.current || buying.current)) return { ok: true };
    buying.current = true;
    if (!isSignedInRef.current) {
      buying.current = false;
      if (!walletRef.current.spendLocal(BUY_IN)) return { ok: false, cost: BUY_IN, balance: walletRef.current.balance };
      ticket.current = { receipt: null, buyin: (ticket.current?.buyin ?? 0) + BUY_IN };
      paid.current += BUY_IN;
      return { ok: true };
    }
    try {
      const r = await api.seatReceipt(tokenRef.current, {
        /*
         * The TABLE's seat count, and it must be the one the settlement sends. This said
         * `seats: 1` from Phase 4 on, while the save sent the real count — so the Worker,
         * which checks the two against each other, refused every signed-in three- and
         * five-seat settlement as `receipt_mismatch`, and those racks never came back.
         */
        which: "blackjack", buyin: BUY_IN, seats, bigBlind: CHIP_VALUE,
        receipt: ticket.current?.receipt ?? undefined,
      });
      ticket.current = { receipt: r.receipt, buyin: r.buyin };
      paid.current += BUY_IN;
      walletRef.current.applied(r.balance, -BUY_IN);
      buying.current = false;
      return { ok: true };
    } catch (e) {
      buying.current = false;
      if (e instanceof ApiError && e.status === 402) {
        const body = e.body as { balance?: number } | null;
        return { ok: false, cost: BUY_IN, balance: Number(body?.balance ?? walletRef.current.balance) };
      }
      void walletRef.current.refresh();
      return { ok: true };
    }
  }, [seats]);

  /**
   * A hand that was still on the table when the player went, counted.
   *
   * Leave is off mid-hand, but the back link, closing the tab and the phone's own back
   * button are not — and every one of those settles the sitting from `session`, which a
   * hand only reaches at `hand_over`. So a player who stood on 16, watched the hole card
   * turn to 20 and pressed back in the second before the chips moved walked away with
   * the stake the engine had already taken. Now: a hand the engine has settled counts
   * at its real result; a hand still waiting on the player loses what is in the circle,
   * which is what walking away from a table does.
   */
  const withOpenHand = (st: Session): Session => {
    const open = bjOpenHand(tableRef.current, countedHand.current);
    return open.hands ? { ...st, hands: st.hands + open.hands, net: st.net + open.net } : st;
  };

  /**
   * One write for a whole sitting, posted on the way out — the poker table's contract
   * and its three exits (Leave, `pagehide`, unmount), idempotent on `saved`.
   *
   * PHASE 8: it settles whenever the sitting holds a ticket, even with no decisions in
   * it. The old guard returned on zero decisions, which was right when a rack was free
   * and meant, once racks were bought, that a player who sat down and left before
   * deciding anything never got their 400 back.
   */
  const saveSitting = useCallback(async (whole: Session) => {
    if (saved.current) return;
    const st = withOpenHand(whole);
    if (st.decisions === 0 && !ticket.current) return;
    saved.current = true;
    const t = ticket.current;
    const gen = sittingGen.current;
    const mine = () => gen === sittingGen.current;
    /* The Worker's own cap (`bjSwing` in routes/game.ts), over the units OPENED. */
    const cap = (buyin: number) => bjMaxOut(buyin, st.betUnits, CHIP_VALUE);

    if (!isSignedInRef.current) {
      if (t) {
        const owed = settleAmount(t.buyin, t.buyin / CHIP_VALUE, st.net, cap(t.buyin));
        if (owed !== null) walletRef.current.creditLocal(owed);
        setSettled({ paid: t.buyin, credited: owed });
      }
      ticket.current = null;
      setSettling(false);
      return;
    }

    setSettling(true);
    try {
      const res = await api.saveBlackjackSitting(tokenRef.current, {
        startedAt: Math.floor(sittingStart.current / 1000),
        endedAt: Math.floor(Date.now() / 1000),
        decks: DOUBLE_DECK_S17.decks,
        dealerStandsSoft17: DOUBLE_DECK_S17.dealerStandsSoft17,
        seats,
        fidelity,
        hands: st.hands,
        decisions: st.decisions,
        correct: st.correct,
        evGiven: st.given,
        net: st.net,
        betUnits: st.betUnits,
        chipValue: CHIP_VALUE,
        receipt: t?.receipt ?? undefined,
        stackOut: t ? settleAmount(t.buyin, t.buyin / CHIP_VALUE, st.net, cap(t.buyin)) : undefined,
      });
      if (res.wallet?.ok && typeof res.wallet.balance === "number") {
        walletRef.current.applied(res.wallet.balance, res.wallet.credited ?? 0);
      }
      /* Only this sitting's ticket is cleared — never the next sitting's new one. */
      if (ticket.current === t) ticket.current = null;
      if (!mine()) return;
      setSettled(t ? { paid: t.buyin, credited: res.wallet?.credited ?? null } : null);
      setSaveNote(res.stored
        ? (st.decisions > 0 ? `Kept ${st.decisions} decisions in your record.` : null)
        : res.reason === "no_consent"
          ? "Not kept — analytics are off in your account settings, so this sitting stayed on this device."
          : null);
    } catch {
      if (!mine()) return;
      saved.current = false;
      if (t) setSettled({ paid: t.buyin, credited: null });
      setSaveNote("Could not settle this sitting — leaving again will retry.");
    }
    if (mine()) setSettling(false);
  }, [seats, fidelity]);

  const saveRef = useRef(saveSitting);
  saveRef.current = saveSitting;

  /* The exits: `pagehide` (mobile, tab discard) and leaving the route inside the app. */
  useEffect(() => {
    const flush = () => { inFlight.current = saveRef.current(sessionRef.current); };
    window.addEventListener("pagehide", flush);
    return () => {
      window.removeEventListener("pagehide", flush);
      director.current?.destroy();
      director.current = null;
      flush();
    };
  }, []);

  /*
   * Back from the browser's page cache. `pagehide` settled the sitting on the way out;
   * a page restored after that must not go on dealing an unfunded table whose next
   * rebuy would buy a rack that could never settle. It goes to the Ledger instead.
   */
  useEffect(() => {
    const back = (e: PageTransitionEvent) => {
      if (!e.persisted || !saved.current || screenRef.current !== "table") return;
      director.current?.destroy();
      director.current = null;
      setScreen("ledger");
    };
    window.addEventListener("pageshow", back);
    return () => window.removeEventListener("pageshow", back);
  }, []);

  /* The record across sittings is Blackjack Pro (§9), and the Worker agrees (402). It is
     asked for only once the answer is known to be yes, and dropped whenever that changes —
     a sign-out, an account switch or a lapsed plan leaves no one else's figures up. */
  useEffect(() => {
    setHistory(null);
    if (!isSignedIn || !exact) return;
    let on = true;
    void api.blackjackSittings(tokenRef.current)
      .then((r) => { if (on && r.totals && r.totals.sittings > 0) setHistory(r.totals); })
      .catch(() => { /* 402 without Blackjack Pro, 401 signed out — both mean "no numbers". */ });
    return () => { on = false; };
  }, [isSignedIn, exact, userId]);

  /* The first tap anywhere unlocks the audio — browsers allow it only in a gesture. */
  useEffect(() => {
    const on = () => unlockSound();
    window.addEventListener("pointerdown", on, { once: true });
    window.addEventListener("keydown", on, { once: true });
    return () => { window.removeEventListener("pointerdown", on); window.removeEventListener("keydown", on); };
  }, []);

  /* ------------------------------------------------------------- grading --- */

  /**
   * Record a decision against its exact analysis — now if the answer is here, or when
   * it lands if it is not. A grade is never skipped for being late: a decision that
   * went unmeasured would silently improve the session score. Each grade is also kept
   * for the hand-end verdict, priced on the bet it was made with.
   */
  const grade = useCallback((full: Decision, took: HeroMove, units: number, offered: readonly HeroMove[], chart?: HeroMove) => {
    /* Priced among the moves the table OFFERED: a double the stack could not cover was
       never a choice, and grading a player against it would charge them for being short. */
    const d = restrictTo(full, offered);
    const cost = costOf(d, took);
    setLastCall({ d, took, chart });
    graded.current.push({ best: d.best, took, cost, bet: units, chart, chartCost: chart ? costOf(d, chart) : undefined });
    const s = sessionRef.current;
    commitSession({
      ...s, decisions: s.decisions + 1,
      correct: s.correct + (cost <= INDIFFERENCE ? 1 : 0),
      given: s.given + cost,
      givenBucks: s.givenBucks + inDollars(cost * units),
    });
  }, []);

  const gradeWhenReady = useCallback((took: HeroMove, units: number, offered: readonly HeroMove[], chart?: HeroMove) => {
    if (analysis) { grade(analysis, took, units, offered, chart); return; }
    const req = live.current;
    if (!req) return;
    const gen = handSeq.current;
    const p = req.result.then((r) => { if (r.decision && gen === handSeq.current) grade(r.decision, took, units, offered, chart); })
      .catch(() => {});
    pending.current.push(p);
  }, [analysis, grade]);

  /**
   * PHASE 9: what basic strategy says for the position on screen, among the moves the
   * table offers — the free verdict's judge (`bjVerdictLine`, `exact: false`). Read
   * BEFORE the move is applied, while the table still shows the position. Basic
   * strategy never takes insurance.
   */
  const chartFor = (what: "insurance" | "act", offered: readonly HeroMove[]): HeroMove | undefined => {
    if (what === "insurance") return "decline";
    const turn = table.turn;
    const up = table.dealerUp;
    if (!turn || up === null) return undefined;
    const cards = table.seats[turn.seat]!.hands[turn.hand]!.cards;
    return basicAction(cards, rankSlot(up >> 2), offered as Action[]);
  };

  /** The units a decision is priced on: the hand being played, or the opening bet. */
  const unitsNow = (what: "insurance" | "act"): number => {
    if (what === "insurance") return handStake.current / CHIP_VALUE;
    const turn = table.turn;
    return turn ? table.seats[turn.seat]!.hands[turn.hand]!.bet : handStake.current / CHIP_VALUE;
  };

  /**
   * The position on screen, analysed while the player looks at it. Keyed on the
   * decision rather than on every snapshot: the director re-renders on every clock
   * tick, and an analysis per tick would be forty enumerations a hand.
   */
  const heroTurn = snap?.phase === "awaiting" && snap.awaiting !== null;
  const decisionId = heroTurn && view
    ? `${view.handNumber}:${snap!.awaiting}:${view.turn?.hand ?? 0}:${view.seats[0]!.hands.map((h) => h.cards.length).join(".")}`
    : null;
  useEffect(() => {
    if (!decisionId) {
      live.current = null;
      setAnalysis(null);
      setAnalysing(false);
      return;
    }
    const req = analyse(snapshot(table));
    live.current = req;
    setAnalysis(null);
    setAnalysing(true);
    setSlowMs(null);
    void req.result.then((r) => {
      if (live.current?.id !== req.id) return;
      setAnalysis(r.decision);
      setAnalysing(false);
      setInlineFallback(r.inline);
      setSlowMs(r.ms >= 150 ? r.ms : null);
    }).catch(() => {
      /* Must still clear `analysing`: study mode holds the bar while it is set. */
      if (live.current?.id !== req.id) return;
      setAnalysing(false);
      setAnalysis(null);
    });
  }, [decisionId, table]);

  /* ----------------------------------------------------- the director's hooks --- */

  /**
   * Everything the director calls back into, off one ref — its options are fixed when
   * it is built and these read state that changes every hand.
   */
  const hooks = useRef({
    onStep: (_s: BjStep, _b: BjView) => {},
    onBot: (_d: BotDecision) => {},
    onHandOver: (_t: Engine) => {},
    onExpire: (_m: HeroMove) => {},
  });

  hooks.current.onStep = (step, before) => {
    flights.emit(step, before);
    const cue = bjCue(step);
    if (cue) playCue(cue);
    switch (step.kind) {
      case "collect":
        setTray((n) => n + before.dealer.length
          + before.seats.reduce((k, s) => k + s.hands.reduce((j, h) => j + h.cards.length, 0), 0));
        break;
      case "shuffle":
        setShoe("shuffling");
        setTray(0);
        window.setTimeout(() => setShoe("full"), Math.max(200, step.dur));
        break;
      case "act": {
        /* A bot's lesson waits for its move to be on the felt. */
        const l = lessons.current.get(step.ordinal);
        if (l && !l.shown) { l.shown = true; if (l.text) pushAt(l.slot, l.text, "lesson"); }
        break;
      }
      /* The hero's chips come back as they land: stake plus result, in Bucks. */
      case "settle": case "insurance":
        if (step.seat === 0 && step.returned > 0) setStack((n) => n + inDollars(step.returned));
        break;
      default: break;
    }
  };

  hooks.current.onBot = ({ seat, action, ordinal, before }) => {
    if (!showLessons) return;
    const slot = feedId.current++;
    const entry = { text: null as string | null, shown: false, slot };
    lessons.current.set(ordinal, entry);
    const gen = handSeq.current;
    const name = cast.names[seat] ?? `Seat ${seat + 1}`;
    const cards = before.seats[seat]!.hands[before.turn!.hand]!.cards;
    const up = before.dealer[0]!;
    void analyse(before).result.then(({ decision }) => {
      /* The hand this describes is over and its feed cleared: say nothing. */
      if (gen !== handSeq.current || !decision) return;
      const cost = costOf(decision, action);
      if (cost <= INDIFFERENCE || decision.best === action) return;
      /* The cost in brackets is one of the exact numbers — Blackjack Pro (§9). */
      const price = exactRef.current ? ` (${money(round2(inDollars(cost)))} for them)` : "";
      const text = `${name} ${action} ${totalLabel(cards)} against the dealer's ` +
        `${rankWord(up)}${price}. It did not change ` +
        "your expectation — their card was random from the remaining deck.";
      entry.text = text;
      if (entry.shown) pushAt(slot, text, "lesson");
    }).catch(() => { /* a lesson that could not be computed is simply not shown */ });
  };

  hooks.current.onExpire = (m) => {
    const what = m === "insurance" || m === "decline" ? "insurance" : "act";
    const offered = offeredFor(what);
    gradeWhenReady(m, unitsNow(what), offered, chartFor(what, offered));
  };

  hooks.current.onHandOver = (t) => {
    countedHand.current = t.handNumber;
    const net = t.net(0);
    const s = sessionRef.current;
    commitSession({ ...s, hands: s.hands + 1, net: s.net + net });
    const hero = t.seats[0]!;
    const words: string[] = [];
    for (const h of hero.hands) {
      const total = totalLabel(h.cards);
      const bust = /over/.test(total);
      const said = h.outcome ? OUTCOME_WORD[h.outcome] : "";
      words.push(bust ? total : `${total} — ${said}`);
      /*
       * A busted hand gets no comparison, because there was none: when every hand is
       * over the dealer turns the hole card and stops, and "27 — over against 8" would
       * report a total the dealer never played to.
       */
      push(bust ? `Over — the hand is done. ${total}.`
        : `${h.outcome === "blackjack" ? "Natural, paid three to two" : h.outcome === "win" ? "Your hand held up"
          : h.outcome === "push" ? "A push" : "The dealer's hand was better"}. ${total} against ${totalLabel(t.dealer)}.`,
        "result");
    }
    /* One line for the band: your hand, what it came to, and what it met — "the dealer
       went over" rather than "against 25 — over", which read as though 25 was a hand. */
    const dealerOver = /over/.test(totalLabel(t.dealer));
    const against = dealerOver ? "the dealer went over" : `against ${totalLabel(t.dealer)}`;
    setResult(words.length === 1 && !/over/.test(words[0]!) ? `${words[0]} · ${against}` : words.join(" · "));
    /* The verdict waits for every grade of this hand, however late the analysis. */
    const gen = handSeq.current;
    void Promise.all(pending.current).then(() => {
      if (gen !== handSeq.current) return;
      setVerdict(bjVerdictLine(graded.current, fmt, exactRef.current));
    });
  };

  /** The director for this sitting. Built when the seat card lets the player in. */
  const startTable = () => {
    director.current?.destroy();
    const d = new BlackjackDirector({
      table,
      fidelity,
      botRng: sitting.botRng,
      seed: sitting.paceSeed,
      durations: () => motionRef.current,
      onChange: (s) => setSnap(s),
      onStep: (s, b) => hooks.current.onStep(s, b),
      onBotDecision: (x) => hooks.current.onBot(x),
      onHandOver: (t) => hooks.current.onHandOver(t),
      onExpire: (m) => hooks.current.onExpire(m),
    });
    director.current = d;
    return d;
  };

  /* ---------------------------------------------------------------- a hand --- */

  /** A hand is on the table, or still being presented. Nothing may throw it away. */
  const presenting = snap?.phase === "playing" || snap?.phase === "awaiting";
  const midHand = presenting || (table.handNumber > 0 && table.phase !== "settled");
  /**
   * Out of chips, checked BETWEEN hands: mid-hand the stack is legitimately short with
   * the bet in the circle. What matters is whether the NEXT hand can be dealt.
   */
  const broke = screen === "table" && !midHand && !canBeDealt(stack);

  /* A stack that has shrunk under the bet takes the bet down with it. */
  useEffect(() => {
    if (midHand || bet <= stack) return;
    const fit = [...BJ_STEPS].reverse().find((s) => s <= stack) ?? BASE_BET;
    setBet(fit);
  }, [stack, bet, midHand]);

  const deal = (d: BlackjackDirector | null = director.current) => {
    if (!d || !d.isIdle || !canBeDealt(stack) || !canCover(stack, stake)) return;
    /*
     * The per-hand state is reset BEFORE the deal, not after it. `director.deal()` can
     * call back synchronously — when the player is dealt a natural the bots play at once,
     * inside the call — and a lesson registered there against the old hand number, into
     * a map about to be cleared, was silently dropped.
     */
    handSeq.current++;
    handStake.current = stake;
    graded.current = [];
    pending.current = [];
    lessons.current.clear();
    setVerdict(null);
    setResult(null);
    setLastCall(null);
    setAnalysis(null);
    setFeed([]);
    /*
     * The opening bet leaves the stack the moment the cards come out, the way it does on
     * a table. Deferring it to settlement would let a player double with money that is
     * already in the circle.
     */
    if (!d.deal(stake / CHIP_VALUE)) return;
    setStack((n) => n - stake);
    const s = sessionRef.current;
    commitSession({ ...s, betUnits: s.betUnits + stake / CHIP_VALUE });
  };

  /** Hit, stand, double, split — or the insurance question's two answers. */
  const onMove = (m: BjMove) => {
    const d = director.current;
    /* The director's own state, not the last render's: the clock may have answered for
       the player a moment ago, and the bar on screen has not caught up yet. */
    if (!d || !d.isAwaiting) return;
    if (m === "insurance" || m === "decline") { onInsurance(m === "insurance"); return; }
    const units = unitsNow("act");
    const offered = offeredFor("act");
    const chart = chartFor("act", offered);
    /* Money and grading follow the move, and only a move the table accepted. Graded
       first, a click that lost the race with the clock took a bet for a double that
       never happened and recorded a decision nobody made. */
    if (!d.act(m)) return;
    gradeWhenReady(m, units, offered, chart);
    /* Doubling and splitting each put a second bet out, the size of the hand's own. */
    if (m === "double" || m === "split") setStack((n) => n - inDollars(units));
  };

  /**
   * Another rack — ADDED to what is left, not substituted for it. A natural pays three
   * to two, so a player can be short of the minimum holding 12.50, and a rebuy that
   * replaced the stack would quietly pocket it. A second buy-in against the wallet, on
   * the same sitting's nonce; below the floor the Barkeep is what the room offers.
   */
  const rebuy = useCallback(() => {
    /* One at a time. Two taps posted the same receipt twice; the Worker debits once and
       answers both, and the room added two racks for the price of one. */
    if (rebuyingRef.current) return;
    rebuyingRef.current = true;
    setRebuying(true);
    void purchase({ rebuy: true }).then((r) => {
      if (!r.ok) { setRackShort(true); return; }
      setRackShort(false);
      setStack((n) => n + BUY_IN);
      setRacks((n) => n + 1);
    }).finally(() => { rebuyingRef.current = false; setRebuying(false); });
  }, [purchase]);

  const onInsurance = (take: boolean) => {
    const d = director.current;
    if (!d || !d.isAwaiting) return;
    if (take && !canCover(stack, handStake.current / 2)) return;
    const units = unitsNow("insurance");
    const offered = offeredFor("insurance");
    if (!d.act(take ? "insurance" : "decline")) return;
    gradeWhenReady(take ? "insurance" : "decline", units, offered, "decline");
    // Half a bet, out of the stack, onto the line.
    if (take) setStack((n) => n - handStake.current / 2);
  };

  /** Leave: stand up, settle, and open the Ledger. One function for every way out. */
  const endSitting = () => {
    director.current?.destroy();
    director.current = null;
    inFlight.current = saveRef.current(sessionRef.current);
    setScreen("ledger");
  };

  /**
   * The same card again: a new sitting, a new seed, a new receipt, one rack.
   *
   * Only once the last sitting has settled. The Ledger's button is live while the
   * settlement is in flight, and starting the next sitting on top of it meant a save
   * that then failed had nothing left to retry with — its ticket was already gone, and
   * so was that rack. So this waits for the save, retries a failed one once, and if it
   * still has not settled stays on the Ledger, where the note says so and pressing the
   * button again tries again.
   */
  const again = async () => {
    if (againing.current) return;
    againing.current = true;
    try {
      await inFlight.current;
      if (!saved.current && ticket.current) {
        inFlight.current = saveRef.current(sessionRef.current);
        await inFlight.current;
      }
      if (!saved.current && ticket.current) return;
      startAgain();
    } finally {
      againing.current = false;
    }
  };

  const startAgain = () => {
    inFlight.current = null;
    director.current?.destroy();
    director.current = null;
    sittingGen.current++;
    countedHand.current = 0;
    ticket.current = null;
    paid.current = 0;
    saved.current = false;
    setSettling(false);
    sittingStart.current = Date.now();
    setSittingNo((n) => n + 1);
    setSnap(null);
    setStack(BUY_IN);
    setRacks(1);
    commitSession(NO_SESSION);
    setFeed([]);
    setVerdict(null);
    setResult(null);
    setLastCall(null);
    setSettled(null);
    setSaveNote(null);
    setRackShort(false);
    setTray(0);
    setScreen("seat");
  };

  /* ----------------------------------------------------------- the band --- */

  /**
   * What the table will actually let you do, which is not the same as what the rules
   * allow. A real dealer refuses a double you cannot match, and refusing it here is
   * what makes a short stack feel short rather than merely look it.
   */
  const heroAct = snap?.awaiting === "act";
  const insuring = snap?.awaiting === "insurance";
  const legal = heroAct
    ? table.legalActions().filter((a) => a === "double" || a === "split" ? canCover(stack, stake) : true)
    : [] as Action[];
  const ruleLegal = heroAct ? table.legalActions() : [] as Action[];
  /* Half a bet, and a stack that cannot cover it is not offered the bet. */
  const canInsure = insuring && canCover(stack, stake / 2);
  /** What the bar offers for a question — the set a decision is graded within. */
  function offeredFor(what: "insurance" | "act"): HeroMove[] {
    if (what === "insurance") return canCover(stack, handStake.current / 2) ? ["insurance", "decline"] : ["decline"];
    return table.legalActions().filter((a) => a === "double" || a === "split" ? canCover(stack, stake) : true);
  }
  /**
   * Study mode is the one place the wait is visible, because it is the one place the
   * answer is wanted BEFORE the decision. Everywhere else the bar stays live and a
   * late answer becomes the grade instead.
   */
  const waiting = analysing && studyOn;
  const shortOf = (m: "double" | "split") => ruleLegal.includes(m) && !legal.includes(m);
  const why: Partial<Record<BjMove, string>> = {
    double: shortOf("double") ? "not enough chips" : "two cards only",
    split: shortOf("split") ? "not enough chips" : "pairs only",
    insurance: insuring && !canInsure ? "not enough chips" : "dealer ace only",
  };
  const risk: Partial<Record<BjMove, string>> = {
    double: `${fmt(stake)} more`, split: `${fmt(stake)} more`, insurance: fmt(stake / 2),
  };
  /* Study mode: each move's exact expectation on this hand's bet, before the choice. */
  /* The analysis of the position on screen, cut to what the bar offers — the same set
     the decision will be graded in, so study mode never recommends a greyed-out Double. */
  const liveAnalysis = analysis && heroTurn ? restrictTo(analysis, offeredFor(insuring ? "insurance" : "act")) : analysis;
  const evs = studyOn && liveAnalysis && heroTurn
    ? Object.fromEntries(liveAnalysis.actions.map((a) => [a.action,
        signedMoney(round2(inDollars(a.ev * unitsNow(insuring ? "insurance" : "act"))))])) as Partial<Record<BjMove, string>>
    : null;
  const between = screen === "table" && !presenting && table.phase === "settled";

  const band = (
    <>
      <BjClockLine snap={snap} view={view} names={cast.names} dealerName={cast.dealer.name} result={between ? result : null} />
      <BetStepper value={stake} steps={BJ_STEPS} open={BJ_STEPPER_SETTLES ? BJ_STEPS.length : 1}
                  max={midHand ? Math.max(stake, stack) : stack} locked={midHand}
                  note={BJ_STEPPER_SETTLES ? null : "Flat 25 a hand for now"}
                  onChange={(v) => setBet(v)} />
      {/*
        * THE BAND NEVER CHANGES HEIGHT — poker's rule. A disabled bar holds the slot
        * whatever is in it, and the live bar, or the verdict and the Deal button
        * between hands, sit in the same grid cell over it.
        */}
      <div className="bk-bandslot">
        {/* The ghost is the TALLEST bar the band can show — the insurance question, every
            sub-line present — so nothing laid over it can make the band grow. */}
        <div className="bk-ghost" aria-hidden="true">
          <ActionBar variant="blackjack" legal={[]} insurance canInsure risk={risk} why={why} busy onMove={() => {}} />
        </div>
        <div className="bk-bandnow">
          {heroTurn ? (
            <ActionBar variant="blackjack" legal={legal as ("hit" | "stand" | "double" | "split")[]}
                       insurance={insuring} canInsure={canInsure}
                       risk={risk} why={why} evs={evs} busy={waiting} onMove={onMove} />
          ) : between && !broke ? (
            <div className="bk-between">
              {/* §7: one line, one tap, about the hand that just ended — poker's rule, so
                  it is silent when the hand gave nothing up worth a line. */}
              {verdict && (
                <p className="td-verdict" role="status">
                  <span>{verdict.text}</span>
                  <Link to={verdict.href}>Drill it</Link>
                </p>
              )}
              {table.phase === "settled" && !broke && (
                <button className="btn bk-deal" type="button" disabled={midHand || broke} onClick={() => deal()}>
                  Deal
                </button>
              )}
            </div>
          ) : (
            <ActionBar variant="blackjack" legal={[]} insurance={false} canInsure={false} why={why} busy onMove={() => {}} />
          )}
        </div>
      </div>
    </>
  );

  /* ------------------------------------------------------ the analysis --- */

  /**
   * WHICH ANALYSIS IS ON SCREEN: the position ahead in study mode, else the decision
   * just made — so the last decision of a hand, the one a player most wants explained,
   * stays up after the hand ends.
   */
  const shown = (studyOn ? liveAnalysis : null) ?? lastCall?.d ?? null;
  const showingLastCall = shown !== null && shown === (lastCall?.d ?? null);

  /* The Ledger reports what was settled: after a page restored from the browser's cache
     that includes the hand `withOpenHand` counted on the way out. */
  const settledSt = withOpenHand(session);
  const summary: SittingSummary = {
    kind: "blackjack",
    hands: settledSt.hands,
    net: inDollars(settledSt.net),
    bb100: null,
    biggestPot: null,
    evLost: settledSt.givenBucks,
    evLostByStreet: { preflop: 0, flop: 0, turn: 0, river: 0 },
    bigBlind: CHIP_VALUE,
    seats,
    stackBb: BUY_IN / CHIP_VALUE,
    cardId: preset.cardId ?? null,
    accuracy: settledSt.decisions > 0 ? settledSt.correct / settledSt.decisions : null,
    evGivenUp: settledSt.decisions > 0 ? round2(settledSt.givenBucks) : null,
    exactLocked: !exact,
  };

  const seatCast = cast.names.map((name, i) => ({
    name,
    avatar: i === 0 ? personaAvatar() : seatAvatar(cast.faces, i, cast.fullNames[i]!),
    isSelf: i === 0,
  }));

  /* ------------------------------------------------------------- render --- */

  return (
    <>
      <div className="room room-table bk-room" style={{ "--room-accent": `var(${room.accent})` } as React.CSSProperties}>
        <RoomHeader
          room={room}
          backTo="/blackjack/play"
          backLabel="Blackjack"
          title={title}
          subtitle={`${seats === 1 ? "Heads up" : `${seats} seats`} · ${cast.dealer.name} deals${table.handNumber ? ` · hand ${table.handNumber}` : ""}`}
          right={screen === "table" ? (
            <button className="room-leave" type="button" disabled={midHand}
                    title={midHand ? "Finish the hand first" : undefined}
                    onClick={endSitting}>Leave</button>
          ) : undefined}
        />

        {screen === "seat" && (
          <SeatFinder
            title={title}
            brief={bjDealerLine(cast.dealer.name)}
            price={{ cost: BUY_IN, line: <>A <BucksAmount n={BUY_IN} /> rack · <BucksAmount n={TABLE_MINIMUM} /> a hand</> }}
            commitment={sitting.commitment}
            cast={seatCast}
            canStake={wallet.canStake}
            preloadNames={cast.fullNames.filter(Boolean)}
            buy={() => purchase()}
            onGo={() => {
              playCue("seat");
              sittingStart.current = Date.now();
              setScreen("table");
              deal(startTable());
            }}
            onBack={() => navigate("/blackjack/play")}
          />
        )}

        {screen === "ledger" && (
          <Ledger
            title={title}
            summary={summary}
            money={settled ?? (paid.current > 0 ? { paid: paid.current, credited: null } : null)}
            settling={settling}
            fmt={fmt}
            cast={[]}
            note={saveNote}
            proof={{
              commitment: sitting.commitment,
              seed: seedHex,
              verifyHref: `/fair?mode=blackjack&hand=${Math.max(1, table.handNumber)}&seed=${seedHex}`,
            }}
            onAgain={again}
            onSaloon={() => navigate("/blackjack/play")}
          />
        )}

        <div className="room-felt" hidden={screen !== "table"}>
          {saveNote && screen === "table" && (
            <div className="room-toast"><p className="muted small" role="status">{saveNote}</p></div>
          )}
          <BlackjackTable
            view={view}
            rules={DOUBLE_DECK_S17}
            names={cast.names}
            faces={cast.faces}
            heroAvatar={personaAvatar()}
            heroStack={stack}
            dealer={cast.dealer}
            standingDealer={standingDealer}
            timers={snap?.clocks}
            shoe={shoe}
            tray={tray}
            chrome="room"
            actions={band}
            flights={flights}
            motion={motion}
          />

          {/*
            * OUT OF CHIPS.
            *
            * What is NOT free is the habit: this is the one mechanic in the room that
            * rehearses chasing a loss, and the counter under the button is the
            * counterweight — not a lecture, just the arithmetic of what the racks would
            * have cost at a table that took money.
            */}
          {broke && (
            <div className="barkeep-scrim" role="presentation">
              <section className="barkeep bj-broke" role="dialog" aria-modal="true" aria-label="Out of chips">
                {/*
                  * "Out of chips" and "short of the minimum" are different sentences: a
                  * natural pays three to two, so odd halves are ordinary.
                  */}
                <p className="barkeep-line">
                  {stack === 0
                    ? "You are out of chips."
                    : `You are short of the minimum — ${money(stack)} left, and the table takes ${money(TABLE_MINIMUM)}.`}
                </p>
                <p className="barkeep-note">
                  Sixteen hands at a {money(TABLE_MINIMUM)} minimum is not a bankroll, it
                  is a coin toss with a small tax on it — and that is the lesson, not a
                  bug. The rack below costs {money(BUY_IN)} Buster Bucks, which are
                  practice money and refill.
                </p>
                {rackShort ? (
                  <>
                    <p className="barkeep-note">You do not have {money(BUY_IN)} Bucks for another rack.</p>
                    <button className="btn barkeep-go" type="button" onClick={() => setBarkeep(true)}>Ask the Barkeep</button>
                  </>
                ) : (
                  <button className="btn barkeep-go" type="button" onClick={rebuy} disabled={rebuying}>
                    Take another {money(BUY_IN)} rack
                  </button>
                )}
                <button className="btn-quiet barkeep-go" type="button" onClick={endSitting}>Stand up</button>
                <p className="barkeep-note">
                  That would be rack {racks + 1}. At a table that took money you would be{" "}
                  {money(racks * BUY_IN)} down, and about to make it{" "}
                  {money((racks + 1) * BUY_IN)}.{" "}
                  <Link to="/responsible-play">Playing within a bankroll</Link>.
                </p>
              </section>
            </div>
          )}
        </div>
      </div>

      {/*
        * Below the felt, where they cost it nothing: the table's own controls, the
        * analysis, the feed, the seed and the room's honesty. Not under the seat card —
        * there is nothing to show before the first hand.
        */}
      {screen !== "seat" && (
        <div className="room-extras bk-extras">
          <Card className="card-wide">
            <p className="eyebrow">The Blackjack Table</p>
            <p className="muted">
              Double-deck blackjack practice with exact per-decision analysis. Two decks, a
              new shuffle before every hand, and every choice checked against the deck that
              is actually left. A {money(TABLE_MINIMUM)} table and a {money(BUY_IN)} rack,
              because sixteen hands of real arithmetic teach more than sixteen hands of
              decimals — none of it is money, and the table is free on every plan.
              {!exact && !unlocks.loading && (
                <> The exact numbers — what each option is worth and what a choice gave
                up — are Blackjack Pro.</>
              )}
            </p>
            {/*
              * Changing seats or opponents is a new table, so it is a new sitting — the
              * seed printed below has to be the seed the hands came from. Disabled
              * mid-hand, because leaving mid-hand would drop a hand whose decisions are
              * already graded.
              */}
            <div className="bj-setup">
              <label className="bj-seats-field">
                <span>Seats</span>
                <select value={seats} disabled={midHand}
                        onChange={(e) => navigate(`/blackjack/play/table?seats=${e.target.value}&fidelity=${fidelity}&card=custom`)}>
                  <option value={1}>Heads up</option>
                  <option value={3}>Three</option>
                  <option value={5}>Five</option>
                </select>
              </label>
              <label className="wide">
                <span>Who's at the table</span>
                <select value={fidelity} disabled={midHand}
                        onChange={(e) => navigate(`/blackjack/play/table?seats=${seats}&fidelity=${e.target.value}&card=custom`)}>
                  {(["sharp", "casual", "loose"] as Fidelity[]).map((f) => (
                    <option key={f} value={f}>{FIDELITY_LABEL[f]}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="bj-toggles">
              {/* Study mode prints the exact numbers first, so it is Blackjack Pro (§9). The
                  pill is a link beside the label, not inside it, so a keyboard reaches it. */}
              <span className="bj-study">
                <label className="check-inline" data-td="bj-study">
                  <input type="checkbox" checked={studyOn} disabled={!exact}
                         aria-describedby={!exact && !unlocks.loading ? "bj-study-pro" : undefined}
                         onChange={(e) => setStudyMode(e.target.checked)} />
                  Show me the numbers before I act
                </label>
                {!exact && !unlocks.loading && (
                  <Link id="bj-study-pro" className="bj-pro-pill" to="/pricing">Blackjack Pro</Link>
                )}
              </span>
              <label className="check-inline">
                <input type="checkbox" checked={showLessons}
                       onChange={(e) => setShowLessons(e.target.checked)} />
                Tell me when another seat gives something up
              </label>
            </div>
            <p className="muted small">
              The other seats are practice opponents at the fidelity you picked, and
              {" "}{cast.dealer.name} deals every hand to the same rule printed on the cloth.
              {studyOn && (
                <> With the numbers on, the buttons wait for the analysis — that is the
                honest cost of seeing them first.</>
              )}
            </p>
          </Card>

          {/* While the unlock is still being asked about, no card: neither the numbers nor
              the lock is known to be right yet. */}
          {(waiting || (shown && (exact || (!unlocks.loading && lastCall)))) && (
            <Card className="card-wide">
              {waiting && <p className="bj-anwait muted small">Working out the deck…</p>}
              {shown && exact && (
                <div className="bj-analysis">
                  <p className="bj-anlabel">
                    Exact — computed from the live deck
                    {shown.indifferent && <em> · either is fine here</em>}
                    {slowMs !== null && <em> · {slowMs}ms of counting</em>}
                  </p>
                  <ul className="bj-evs">
                    {shown.actions.map((a) => {
                      const took = showingLastCall && lastCall?.took === a.action;
                      const best = shown.best === a.action;
                      return (
                        <li key={a.action} className={took ? "took" : best ? "best" : ""}>
                          <span className="bj-ev-name">
                            {a.action === "decline" ? "Declining" : a.action.charAt(0).toUpperCase() + a.action.slice(1)}
                          </span>
                          {/* In Bucks on one chip, because the table is in Bucks. */}
                          <span className="num">{signedMoney(round2(inDollars(a.ev)))}</span>
                          {took && <em>your choice</em>}
                          {!took && best && <em>best</em>}
                        </li>
                      );
                    })}
                  </ul>
                  {inlineFallback && (
                    <p className="muted small">
                      This browser is doing the counting on the page itself rather than in a
                      background thread, so a split may pause for a moment. The numbers are
                      the same either way.
                    </p>
                  )}
                  {studyOn && lastCall && !showingLastCall && (() => {
                    const c = costOf(lastCall.d, lastCall.took);
                    const name = lastCall.took === "decline" ? "declining" : lastCall.took;
                    return c <= INDIFFERENCE
                      ? <p className="muted small">Your last call — {name} — was the best of them.</p>
                      : (
                        <p className="muted small">
                          Your last call — {name} — gave up{" "}
                          <span className="num">{money(round2(inDollars(c)))}</span> against{" "}
                          {lastCall.d.actions[0]!.action}.
                        </p>
                      );
                  })()}
                  {showingLastCall && lastCall && (() => {
                    const c = costOf(lastCall.d, lastCall.took);
                    return c <= INDIFFERENCE
                      ? <p className="muted small">That was the best of them, or close enough that it does not matter.</p>
                      : (
                        <p className="muted small">
                          That gave up <span className="num">{money(round2(inDollars(c)))}</span>{" "}
                          against {lastCall.d.actions[0]!.action}.
                        </p>
                      );
                  })()}
                </div>
              )}
              {/*
                * PHASE 9: without Blackjack Pro the card says what basic strategy says about
                * the last decision, and names what the exact numbers would add. Nothing on
                * it is a figure the unlock sells.
                */}
              {shown && !exact && !unlocks.loading && lastCall && (
                <div className="bj-analysis bj-analysis-locked" data-td="bj-locked">
                  <p className="bj-anlabel">Your last call</p>
                  <p>
                    You {verbed(lastCall.took)}.{" "}
                    {lastCall.chart
                      ? lastCall.chart === lastCall.took
                        ? <>That is what basic strategy does.</>
                        : <>Basic strategy {chartVerb(lastCall.chart)}.</>
                      : null}
                  </p>
                  <p className="muted small">
                    <b>Blackjack Pro</b> prints the exact expected value of every option,
                    worked out from the deck that is left, and what each choice gave up —
                    plus the Count Drill, Decision Spots and your record across sittings.{" "}
                    {usd(UNLOCKS.blackjack_pro.amountCents)} once, or included in Poker Pro.{" "}
                    <Link to="/pricing">See Blackjack Pro</Link>
                  </p>
                </div>
              )}
            </Card>
          )}

          {feed.length > 0 && (
            <Card className="card-wide">
              <ul className="bj-feed">
                {feed.map((f) => <li key={f.id} className={`bj-f-${f.kind}`}>{f.text}</li>)}
              </ul>
            </Card>
          )}

          {/*
            * PHASE 9 box 0(1): the COMMITMENT while the sitting runs, the seed on the
            * Ledger once it is over — poker's pattern. This card printed the seed from
            * the first hand, and a seed replayed through the shoe tells you every hole
            * card; with the stepper open that is worth real Bucks. On the Ledger screen
            * the card is gone: the Ledger carries the seed and the "Check this shoe" link.
            */}
          {screen === "table" && (
          <Card className="card-wide">
            <h2>Where these cards came from</h2>
            <p className="muted small">
              One 256-bit seed was drawn when you sat down, and every hand of the sitting is
              derived from it — hand {table.handNumber || 1} from that seed and the
              hand&rsquo;s own label. Nothing is drawn while a hand is running, so the shoe
              was fixed before you saw a card. This is the seed&rsquo;s commitment, the same
              one the seat card showed; the seed itself is on the Ledger when you stand up,
              and you can deal every hand again from it.
            </p>
            <p className="num bj-seed">{sitting.commitment}</p>
          </Card>
          )}

          <Card className="card-wide">
            <h2>What this room does not do</h2>
            <ul className="claims-list">
              <li>
                <strong>No counting.</strong> The deck is reshuffled before every hand, so
                there is nothing to carry forward and no count to keep. That is on purpose,
                and it is why the analysis can be exact about the deck in front of you. The
                Count Drill in the Train tab is an exercise, not an edge at this table.
              </li>
              <li>
                <strong>No bet advice.</strong>{" "}
                {BJ_STEPPER_SETTLES ? (
                  <>The stepper moves your stake between hands. Under a fresh shuffle,
                  varying it changes nothing about your edge — only how far the stack swings —
                  and this room will never tell you when to press.</>
                ) : (
                  <>Every hand is one green chip. Under a fresh shuffle, varying your stake
                  changes nothing about your edge, so this table does not offer you the choice
                  yet — and a room that sold you a betting system on top of a shuffled deck
                  would be selling theatre.</>
                )}
              </li>
              <li>
                <strong>None of it is money.</strong> The chips say {money(CHIP_VALUE)} and
                the rack costs {money(BUY_IN)}, in Buster Bucks — the same practice money
                that funds every room here. That is what the arithmetic looks like in a real
                room, and a stack that goes down is the only way to learn what variance
                feels like. There is nothing to buy, nothing to take out, and no prize.
                Buster Bucks are practice money. They refill. When you run out, the Barkeep
                stakes you, as many racks as you need.
              </li>
            </ul>
          </Card>

          {session.decisions > 0 && (
            <Card className="card-wide">
              <h2>This sitting</h2>
              {session.decisions < 30 ? (
                <Notice kind="info">
                  {session.decisions} decision{session.decisions === 1 ? "" : "s"} so far.
                  Counts only until there are thirty — below that a rate says more about the
                  cards than about you.
                </Notice>
              ) : (
                <ul className="stat-row">
                  <li><span>Hands</span><b className="num">{session.hands}</b></li>
                  <li><span>Decisions</span><b className="num">{session.decisions}</b></li>
                  <li><span>Matched the exact play</span><b className="num">{((session.correct / session.decisions) * 100).toFixed(0)}%</b></li>
                  <li><span>Given up</span><b className="num">{exact ? money(round2(session.givenBucks)) : unlocks.loading ? "—" : "Blackjack Pro"}</b></li>
                  <li><span>Stack</span><b className="num">{money(stack)}</b></li>
                </ul>
              )}
              {history && exact ? (
                <>
                  <h3>Across your sittings</h3>
                  <ul className="stat-row">
                    <li><span>Sittings</span><b className="num">{history.sittings}</b></li>
                    <li><span>Decisions</span><b className="num">{history.decisions}</b></li>
                    <li>
                      <span>Matched the exact play</span>
                      <b className="num">
                        {history.decisions > 0 ? `${((history.correct / history.decisions) * 100).toFixed(0)}%` : "—"}
                      </b>
                    </li>
                    <li><span>Given up</span><b className="num">{money(round2(inDollars(history.ev_given)))}</b></li>
                  </ul>
                </>
              ) : unlocks.loading ? null : exact ? (
                <p className="muted small">
                  Your record <em>across</em> sittings starts here: it appears once a
                  sitting has been saved to your account.
                </p>
              ) : (
                <p className="muted small">
                  The table and the chart&rsquo;s verdict are free on every plan, with or
                  without an account. Keeping the record <em>across</em> sittings — what your
                  mistake rate has been doing over weeks — is part of Blackjack Pro, with the
                  exact numbers: {usd(UNLOCKS.blackjack_pro.amountCents)} once, or included
                  in Poker Pro.{" "}
                  <Link to="/pricing">See Blackjack Pro</Link>.
                </p>
              )}
            </Card>
          )}
        </div>
      )}

      {barkeep && <Barkeep onClose={() => { setBarkeep(false); setRackShort(false); }} />}
    </>
  );
}
